'use client';

import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import type { ReactNode } from 'react';
import {
    assignHidePrioritiesFromRight,
    getMegaMenuColumnHideClass,
    MEGA_MENU_COLUMN_GAP_CLASS,
    MEGA_MENU_COLUMN_WIDTH_CLASS,
    MEGA_MENU_DROPDOWN_HEIGHT_CLASS,
    MEGA_MENU_DROPDOWN_PADDING_CLASS,
} from '../../../lib/mega-menu-layout';

export type MegaMenuDropdownColumn = {
    key: string;
    content: ReactNode;
    hidePriority?: number;
    /** Lets a start-column background clip at the dropdown edges instead of the inner padding. */
    bleedStart?: boolean;
};

export type MegaMenuSkeletonColumnType = 'links' | 'feature';

type MegaMenuDropdownLayoutProps = {
    columns: MegaMenuDropdownColumn[];
    className?: string;
};

const LINK_SKELETON_ROW_COUNT = 8;
const DROPDOWN_SHELL_CLASS = cn(
    'flex w-full justify-center overflow-hidden',
    MEGA_MENU_DROPDOWN_PADDING_CLASS,
    MEGA_MENU_DROPDOWN_HEIGHT_CLASS,
    MEGA_MENU_COLUMN_GAP_CLASS
);

export function MegaMenuLinkColumnSkeleton() {
    return (
        <div aria-hidden="true" className="flex w-full flex-col gap-4" data-slot="mega-menu-link-column-skeleton">
            <Skeleton className="h-5 w-40 rounded-none" />
            <div className="flex flex-col gap-3">
                {Array.from({ length: LINK_SKELETON_ROW_COUNT }, (_, index) => (
                    <Skeleton key={index} className="h-4 w-48 rounded-none" />
                ))}
            </div>
        </div>
    );
}

export function MegaMenuFeatureColumnSkeleton() {
    return (
        <div
            aria-hidden="true"
            className="flex h-full w-full flex-col gap-4"
            data-slot="mega-menu-feature-column-skeleton">
            <Skeleton className="aspect-square w-full rounded-none" />
            <Skeleton className="h-3 w-28 rounded-none" />
            <Skeleton className="h-6 w-3/4 rounded-none" />
            <Skeleton className="h-4 w-full rounded-none" />
            <Skeleton className="h-4 w-5/6 rounded-none" />
            <Skeleton className="h-4 w-24 rounded-none" />
        </div>
    );
}

export function MegaMenuDropdownLayout({ columns, className }: MegaMenuDropdownLayoutProps) {
    if (columns.length === 0) {
        return null;
    }

    const columnsWithPriority = assignHidePrioritiesFromRight(columns);

    return (
        <div data-slot="mega-menu-dropdown-layout" className={cn(DROPDOWN_SHELL_CLASS, className)}>
            {columnsWithPriority.map((column) => (
                <div
                    key={column.key}
                    data-slot={column.bleedStart ? 'mega-menu-bleed-start-column' : undefined}
                    className={cn(
                        MEGA_MENU_COLUMN_WIDTH_CLASS,
                        'min-w-0 shrink-0',
                        column.bleedStart ? 'relative h-full' : 'overflow-hidden',
                        getMegaMenuColumnHideClass(column.hidePriority)
                    )}>
                    {column.content}
                </div>
            ))}
        </div>
    );
}

export function MegaMenuDropdownSkeleton({
    columns = ['links', 'links', 'links', 'feature'],
}: {
    columns?: MegaMenuSkeletonColumnType[];
}) {
    const columnsWithPriority = assignHidePrioritiesFromRight(
        columns.map((type, index) => ({ key: String(index), type }))
    );

    return (
        <div data-slot="mega-menu-dropdown-skeleton" aria-hidden="true" className={DROPDOWN_SHELL_CLASS}>
            {columnsWithPriority.map((column) => (
                <div
                    key={column.key}
                    className={cn(
                        MEGA_MENU_COLUMN_WIDTH_CLASS,
                        'min-w-0 shrink-0 overflow-hidden',
                        getMegaMenuColumnHideClass(column.hidePriority)
                    )}>
                    {column.type === 'feature' ? <MegaMenuFeatureColumnSkeleton /> : <MegaMenuLinkColumnSkeleton />}
                </div>
            ))}
        </div>
    );
}
